import { Link } from 'react-router-dom';
import { useT } from '@/i18n';
import { useSession } from '@/hooks/session';
import { AppShell } from '@/components/layout';
import { Button, Check, Download, Heart, Illustration, Lock, Pencil, Shield, Trash, Users } from '@/components/ui';
import type { IconProps } from '@/components/ui';
import { CrisisNote } from '@/components/trust/CrisisNote';
import { GrievanceCard } from '@/components/trust/GrievanceCard';
import { sceneImage } from '@/lib/illustrations';
import { COMPANY } from '@/content/company';

type Icon = (p: IconProps) => JSX.Element;

const RIGHTS: Array<{ key: string; Icon: Icon; to: string; signedOutTo: string }> = [
  { key: 'see', Icon: Download, to: '/settings#your-data', signedOutTo: '/signin' },
  { key: 'fix', Icon: Pencil, to: '/settings', signedOutTo: '/data-request' },
  { key: 'erase', Icon: Trash, to: '/settings#your-data', signedOutTo: '/data-request' },
  { key: 'withdraw', Icon: Lock, to: '/settings#privacy', signedOutTo: '/data-request' },
  { key: 'nominate', Icon: Users, to: '/settings#privacy', signedOutTo: '/signin' },
];

const MADE = ['same', 'plain', 'clinicians', 'notDiagnosis'] as const;

/** Why a parent can trust Nurture with their child's story — modelled on zenquip.com/trust. */
export const Trust = () => {
  const t = useT();
  const { account } = useSession();

  return (
    <AppShell wide>
      {/* Hero */}
      <div className="mx-auto grid max-w-content items-center gap-10 px-4 pb-14 pt-10 md:px-6 nav:grid-cols-[1.15fr_1fr] nav:pt-16">
        <div>
          <p className="eyebrow text-lagoon-600">{t('trust.eyebrow')}</p>
          <h1 className="mt-3 text-hero">{t('trust.title')}</h1>
          <p className="mt-5 max-w-[34em] text-[18px] leading-relaxed text-ink-soft">{t('trust.sub')}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button as="a" href="/privacy" variant="secondary">
              {t('trust.readPolicy')}
            </Button>
            <Link to="/data-request" className="inline-flex min-h-btn items-center px-2 text-label font-extrabold text-lagoon-600 hover:underline">
              {t('footer.dataRequest')} →
            </Link>
          </div>
        </div>
        <div aria-hidden="true" className="relative mx-auto h-[360px] w-full max-w-[340px]">
          <div className="absolute inset-x-6 top-0 h-[330px] overflow-hidden rounded-arch bg-white shadow-[0_0_0_8px_#dbeafe]">
            <Illustration src={sceneImage('report-ready-lap')} alt="" position="50% 35%" eager />
          </div>
          <div className="absolute -left-2 bottom-2 flex items-center gap-2 rounded-pill bg-white py-2 pl-2 pr-4 text-label shadow-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-lagoon-600 text-white">
              <Lock className="h-4 w-4" />
            </span>
            {t('trust.badge')}
          </div>
        </div>
      </div>

      {/* Promises: one lead promise, two supporting */}
      <section aria-labelledby="promises" className="bg-white">
        <div className="mx-auto max-w-content px-4 py-16 md:px-6">
          <h2 id="promises" className="text-display">{t('trust.promises.title')}</h2>
          <div className="mt-8 grid gap-4 nav:grid-cols-[1.35fr_1fr] nav:grid-rows-2">
            <article className="rounded-xl bg-lagoon-700 p-8 text-white nav:row-span-2">
              <Shield className="h-9 w-9 text-lagoon-200" />
              <h3 className="mt-6 text-h2 text-white">{t('trust.p1.title')}</h3>
              <p className="mt-2 max-w-[30em] text-body text-lagoon-100">{t('trust.p1.body')}</p>
              <ul className="mt-6 grid gap-2 text-label">
                {(['a', 'b', 'c'] as const).map((k) => (
                  <li key={k} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-lagoon-200" />
                    {t(`trust.p1.${k}`)}
                  </li>
                ))}
              </ul>
            </article>
            {(['p2', 'p3'] as const).map((p) => (
              <article key={p} className="rounded-xl bg-paper p-6">
                <h3 className="text-h3">{t(`trust.${p}.title`)}</h3>
                <p className="mt-2 text-body text-ink-soft">{t(`trust.${p}.body`)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* DPDP rights, each with the way to use it */}
      <section aria-labelledby="rights" className="mx-auto max-w-content px-4 py-16 md:px-6">
        <div className="grid gap-8 nav:grid-cols-[1fr_1.5fr]">
          <div>
            <p className="eyebrow text-sage-600">{t('trust.rights.eyebrow')}</p>
            <h2 id="rights" className="mt-2 text-display">{t('trust.rights.title')}</h2>
            <p className="mt-3 text-body text-ink-soft">{t('trust.rights.body')}</p>
          </div>
          <ul className="divide-y divide-line rounded-lg border border-line bg-white">
            {RIGHTS.map(({ key, Icon, to, signedOutTo }) => (
              <li key={key}>
                <Link to={account ? to : signedOutTo} className="group grid grid-cols-[40px_1fr_auto] items-center gap-4 px-5 py-4 hover:bg-mist">
                  <span aria-hidden="true" className="flex h-10 w-10 items-center justify-center rounded-md bg-sage-100 text-sage-600">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-label text-ink">{t(`trust.right.${key}`)}</span>
                    <span className="block text-caption text-ink-muted">{t(`trust.right.${key}.how`)}</span>
                  </span>
                  <span aria-hidden="true" className="text-lagoon-600 transition-transform duration-fade group-hover:translate-x-1">→</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How the report is made */}
      <section aria-labelledby="made" className="bg-white">
        <div className="mx-auto grid max-w-content gap-10 px-4 py-16 md:px-6 nav:grid-cols-[1fr_1.2fr]">
          <div>
            <p className="eyebrow text-coral-600">{t('l2.trust0')}</p>
            <h2 id="made" className="mt-2 text-display">{t('trust.made.title')}</h2>
            <p className="mt-3 text-body text-ink-soft">{t('trust.made.body')}</p>
            <Link to="/terms#medical-disclaimer" className="mt-4 inline-flex min-h-tap items-center text-label font-extrabold text-lagoon-600 hover:underline">
              {t('trust.made.link')} →
            </Link>
          </div>
          <ol className="grid gap-6 sm:grid-cols-2">
            {MADE.map((k, i) => (
              <li key={k} className="border-t-[3px] border-sage-300 pt-4">
                <span className="font-display text-[15px] font-extrabold tabular-nums text-sage-500">0{i + 1}</span>
                <h3 className="mt-1 text-h3">{t(`trust.made.${k}.title`)}</h3>
                <p className="mt-1 text-body text-ink-soft">{t(`trust.made.${k}.body`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Urgent help, and a person to talk to */}
      <section className="mx-auto grid max-w-content gap-6 px-4 py-16 md:px-6 nav:grid-cols-2">
        <div className="rounded-lg bg-coral-100 p-6 text-ink">
          <p className="flex items-center gap-2 text-h3">
            <Heart className="h-6 w-6 text-coral-600" />
            {t('trust.urgent.title')}
          </p>
          <CrisisNote className="mt-3 text-[14px] text-ink" />
        </div>
        <GrievanceCard id="grievance-officer" />
      </section>

      <p className="mx-auto max-w-content px-4 pb-14 text-caption text-ink-muted md:px-6">
        {t('trust.company', { name: COMPANY.legalName, cin: COMPANY.cin })}
      </p>
    </AppShell>
  );
};

export default Trust;
